import { useState } from 'react';
import { waSentMessages } from '../../../mocks/whatsapp';
import type { WaMessageStatus, WaTemplateCategory } from '../../../mocks/whatsapp';

interface Props {
  onResend: (messageId: string) => void;
}

const statusConfig: Record<WaMessageStatus, { label: string; color: string; icon: string }> = {
  Read: { label: 'Read', color: 'bg-blue-50 text-blue-700', icon: 'ri-check-double-line' },
  Delivered: { label: 'Delivered', color: 'bg-emerald-50 text-emerald-700', icon: 'ri-check-double-line' },
  Sent: { label: 'Sent', color: 'bg-gray-100 text-gray-600', icon: 'ri-check-line' },
  Pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700', icon: 'ri-time-line' },
  Failed: { label: 'Failed', color: 'bg-red-50 text-red-700', icon: 'ri-close-circle-line' },
};

const categoryColors: Record<WaTemplateCategory, string> = {
  Booking: 'bg-emerald-50 text-emerald-700',
  Payment: 'bg-amber-50 text-amber-700',
  Reminder: 'bg-indigo-50 text-indigo-700',
  Visa: 'bg-violet-50 text-violet-700',
  Custom: 'bg-rose-50 text-rose-700',
};

const statusFilters: (WaMessageStatus | 'All')[] = ['All', 'Read', 'Delivered', 'Sent', 'Pending', 'Failed'];

export default function MessageHistory({ onResend }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WaMessageStatus | 'All'>('All');

  const filtered = waSentMessages.filter((m) => {
    const matchSearch =
      m.recipientName.toLowerCase().includes(search.toLowerCase()) ||
      m.templateName.toLowerCase().includes(search.toLowerCase()) ||
      (m.bookingRef || '').toLowerCase().includes(search.toLowerCase()) ||
      m.recipientPhone.includes(search);
    const matchStatus = statusFilter === 'All' || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts: Record<string, number> = { All: waSentMessages.length };
  statusFilters.slice(1).forEach((s) => {
    counts[s] = waSentMessages.filter((m) => m.status === s).length;
  });

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search by name, phone, booking ref, template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300"
          />
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {statusFilters.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === s
                ? 'text-white border-transparent'
                : 'text-gray-500 border-gray-200 bg-white hover:bg-gray-50'
            }`}
            style={statusFilter === s ? { backgroundColor: '#25D366', borderColor: '#25D366' } : {}}
          >
            {s} <span className="opacity-70">({counts[s] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Recipient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Template / Booking</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent At</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400 text-sm">
                  <i className="ri-chat-off-line text-3xl block mb-2 text-gray-300" />
                  No messages found
                </td>
              </tr>
            ) : (
              filtered.map((msg) => {
                const sc = statusConfig[msg.status];
                return (
                  <tr key={msg.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    {/* Recipient */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
                          {msg.recipientAvatar}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{msg.recipientName}</p>
                          <p className="text-xs text-gray-400">{msg.recipientPhone}</p>
                        </div>
                      </div>
                    </td>

                    {/* Template / booking */}
                    <td className="px-4 py-3">
                      <div>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mb-1 ${categoryColors[msg.category]}`}>
                          {msg.templateName}
                        </span>
                        <p className="text-xs text-gray-500 max-w-xs truncate">{msg.previewText}</p>
                        {msg.bookingRef && (
                          <span className="text-xs text-gray-400 mt-0.5 block">{msg.bookingRef} · {msg.destination}</span>
                        )}
                      </div>
                    </td>

                    {/* Sent at */}
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{msg.sentAt}</td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${sc.color}`}>
                          <i className={`${sc.icon} text-sm`} />
                          {sc.label}
                        </span>
                        {msg.readAt && (
                          <p className="text-xs text-gray-400 mt-0.5">Read {msg.readAt.split(' ')[1]}</p>
                        )}
                      </div>
                    </td>

                    {/* Agent */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                          {msg.agentAvatar}
                        </div>
                        <span className="text-xs text-gray-500">{msg.agent}</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onResend(msg.id)}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-send-plane-line mr-1" />
                        Resend
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3 text-right">Showing {filtered.length} of {waSentMessages.length} messages</p>
    </div>
  );
}
